import {
  CategorizationMap,
  toRadioChip,
} from "portal/utils/categoryCollection/category";
import { CategorizedImages } from "../categorized/CategorizedImages";
import {
  Dimensions,
  imagesToCategoryIds,
} from "portal/utils/categoryCollection/image";
import { getTemplateVersionCard } from "./categoryVersionDiff";
import { portalEndpoints } from "portal/state/portalApi";
import { ShootTimeHistorySection } from "./ShootTimeHistorySection";
import { Skeleton } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useShootTimeExpanded } from "../useShootTimeExpanded";
import { useTranslation } from "react-i18next";
import { useVersionHistory } from "./VersionHistoryProvider";
import { VersionHeader } from "./VersionHeader";
import { VersionHistoryList } from "./VersionHistoryList";
import React, { FC, useMemo, useState } from "react";

interface CategoryTemplateHistoryProps {
  uuid: string;
  dimensions: Dimensions;
  onRevert?: (revertToVersionId: string) => Promise<unknown>;
  isReverting: boolean;
  currentVersionCategorizationMap: CategorizationMap;
  onClassify?: (
    predictionPointId?: string,
    categoryId?: string
  ) => void | Promise<unknown>;
}

export const CategoryTemplateHistory: FC<CategoryTemplateHistoryProps> = ({
  uuid,
  dimensions,
  onRevert,
  isReverting,
  currentVersionCategorizationMap,
  onClassify,
}) => {
  const { i18n, t } = useTranslation();
  const { selectedVersionId, refetchVersions } = useVersionHistory();
  const [showDiff, setShowDiff] = useState(true);

  const { currentData: selectedVersion, isFetching: isSelectedVersionLoading } =
    useQueryPopups(
      portalEndpoints.getCategoryVersion.useQuery(
        selectedVersionId
          ? {
              uuid,
              cvUuid: selectedVersionId,
            }
          : skipToken
      )
    );

  const compareVersionId = selectedVersion?.metadata?.parentVersionId;

  const {
    currentData: compareWithVersion,
    isFetching: isCompareVersionLoading,
  } = useQueryPopups(
    portalEndpoints.getCategoryVersion.useQuery(
      compareVersionId
        ? {
            uuid,
            cvUuid: compareVersionId,
          }
        : skipToken
    )
  );

  const category = selectedVersion?.category;
  const categoryOptions = useMemo(
    () => (category ? toRadioChip(i18n, t, [category], false) : []),
    [category, i18n, t]
  );
  const categorizationMap = useMemo(
    () => (category ? imagesToCategoryIds([category]) : {}),
    [category]
  );
  const card = useMemo(() => {
    if (!category) {
      return;
    }
    const versus =
      showDiff && compareWithVersion?.category
        ? compareWithVersion.category
        : undefined;
    return getTemplateVersionCard(category, versus);
  }, [category, compareWithVersion?.category, showDiff]);

  const {
    isShootTimeExpanded,
    allShootTimeExpanded,
    setAllShootTimeExpanded,
    onShootTimeExpandedChange,
  } = useShootTimeExpanded(card ? [card.categoryId] : []);

  return (
    <div className="flex flex-col md:flex-row">
      <div className="w-full md:w-1/3 md:min-w-72 mb-4 md:mb-0">
        <div className="h-80 md:h-fit min-h-0 overflow-y-auto">
          <VersionHistoryList />
        </div>
      </div>

      <div className="w-full pl-2">
        {selectedVersion?.metadata && (
          <VersionHeader
            metadata={selectedVersion.metadata}
            onRevert={onRevert}
            isReverting={isReverting}
            showDiff={showDiff}
            onToggleDiff={(show) => setShowDiff(show)}
            hasShootTime={Boolean(card?.shootTime)}
            shootTimeExpanded={allShootTimeExpanded}
            onToggleShootTime={() => {
              setAllShootTimeExpanded(!allShootTimeExpanded);
            }}
          />
        )}
        <div className="w-full flex flex-col gap-4 mt-3">
          {isSelectedVersionLoading || isCompareVersionLoading ? (
            <Skeleton variant="rectangular" className="w-full h-32" />
          ) : (
            card && (
              <CategorizedImages
                categoryId={card.categoryId}
                categoryName={card.name}
                categoryOptions={categoryOptions}
                predictionPointIds={card.predictionPointIds}
                dimensions={dimensions}
                categorizationMap={categorizationMap}
                versus={card.versus}
                onClassify={
                  onClassify
                    ? async (
                        predictionPointId: string,
                        nextCategoryId: string
                      ) => {
                        await onClassify(predictionPointId, nextCategoryId);
                        refetchVersions();
                      }
                    : undefined
                }
                classificationAction="revive"
                rightClickMenuTitle={t(
                  "components.categoryTemplates.actions.revive"
                )}
                isRightClickDisabled={(predictionPointId: string) =>
                  Boolean(currentVersionCategorizationMap[predictionPointId])
                }
              >
                {card.shootTime && (
                  <ShootTimeHistorySection
                    shootTime={card.shootTime}
                    expanded={isShootTimeExpanded(card.categoryId)}
                    onExpandedChange={(expanded) => {
                      onShootTimeExpandedChange(card.categoryId, expanded);
                    }}
                  />
                )}
              </CategorizedImages>
            )
          )}
        </div>
      </div>
    </div>
  );
};
